import React, { useContext, useEffect, useMemo, useState } from "react";
import { IoSearchOutline } from "react-icons/io5";
import { useNavigate } from "react-router-dom";
import ClientAuthContext from "../../context/ClientAuthContext";
import "../../css/items.scss";
import { serverInstance } from "../../service/axios";
import "./mock-test-list.scss";
import {
    getExamEntryWindowUnixMs,
    isExamEntryOpen
} from "./mockTestSchedule";

const toDateLabel = (value) => {
  if (!value) {
    return "-";
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "-";
  }
  return date.toLocaleDateString();
};

const getTypeDisplayLabel = (type, labels) => {
  if (type === "MOCK") {
    return labels.typeMock || "БЭЛТГЭЛ";
  }
  if (type === "EXAM") {
    return labels.typeExam || "ШАЛГАЛТ";
  }
  return String(type || "");
};

export default function MockTestList({ data }) {
  const navigate = useNavigate();
  const { userData } = useContext(ClientAuthContext);
  const labels = data?.MockTest || {};
  const isLoggedIn = Boolean(userData?.email);
  const isMembershipActive = userData?.paid_until && new Date(userData.paid_until) > new Date();
  const canUseTests = isLoggedIn && isMembershipActive;

  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [nowMs, setNowMs] = useState(() => Date.now());

  useEffect(() => {
    const t = setInterval(() => setNowMs(Date.now()), 30000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    setLoading(true);
    setError("");

    serverInstance
      .get("/client/tests", {
        headers: {
          "Content-Type": "application/json"
        }
      })
      .then((res) => {
        const payload = Array.isArray(res.data?.data) ? res.data.data : [];
        const sorted = [...payload].sort((a, b) => Number(a.index) - Number(b.index));
        setTests(sorted);
      })
      .catch((err) => {
        const message =
          err?.response?.data?.message ||
          err?.response?.data ||
          "Mock test жагсаалт авах үед алдаа гарлаа.";
        setError(typeof message === "string" ? message : "Mock test жагсаалт авах үед алдаа гарлаа.");
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  const filteredTests = useMemo(() => {
    const visible = tests.filter((test) => {
      if (test.type !== "EXAM") {
        return true;
      }
      // Always show EXAM in list when schedule datetime exists.
      return Boolean(String(test.test_dt ?? "").trim());
    });
    const q = query.trim().toLowerCase();
    if (!q) {
      return visible;
    }
    return visible.filter((test) => {
      const name = (test.name || "").toLowerCase();
      const description = (test.description || "").toLowerCase();
      return name.includes(q) || description.includes(q);
    });
  }, [query, tests, nowMs, canUseTests]);

  const getOpenDisabledReason = (test) => {
    if (!isLoggedIn) {
      return labels.loginRequired || "Тест нээхийн тулд нэвтэрнэ үү.";
    }
    if (!isMembershipActive) {
      return labels.payment || "Гишүүнчлэл идэвхтэй хэрэглэгч тест нээх боломжтой.";
    }
    if (test.type === "EXAM") {
      const w = getExamEntryWindowUnixMs(test.test_dt, test.test_minutes);
      if (!w) {
        return labels.examScheduleInvalid || "Шалгалтын хуваарь тохируулаагүй байна.";
      }
      if (nowMs < w.openMs) {
        return labels.examNotYetOpen || "Шалгалт зөвхөн эхлэх цагтаа нээгдэнэ (УБ цаг).";
      }
      if (nowMs >= w.closeMs) {
        return labels.examPeriodEnded || "Шалгалтыг нээх хугацаа дууссан (дараагийн өдрийн 00:00 УБ).";
      }
    }
    return null;
  };

  const isRowOpenEnabled = (test) => getOpenDisabledReason(test) === null;

  return (
    <div className="items-container" id="mock-tests">
      <section className="upper-section">
        <div className="upper-section-inner">
          <div className="title-container">
            <h2>{labels.title || "Шалгалтын материалууд"}</h2>
          </div>

          <div className="input-container">
            <input
              type="text"
              placeholder={labels.searchPlaceholder || "Сорил шалгалт хайх..."}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            <button className="search-btn" type="button" aria-label={labels.searchButton || "Search"}>
              <IoSearchOutline size={18} />
            </button>
          </div>
        </div>
      </section>

      <div className="main-container">
        <div className="search-result">
          {loading ? (
            <div className="d-flex justify-content-center align-items-center gap-2 text-secondary py-4">
              <span>{labels.loadingList || "Жагсаалт ачаалж байна..."}</span>
            </div>
          ) : null}

          {error ? <div className="alert alert-danger">{error}</div> : null}
          {!isLoggedIn ? (
            <div className="alert alert-info mb-3">
              {labels.practiceAuthNotice ||
                "Сорил дасгал ажиллахын өмнө нэвтэрч, төлбөртэй гишүүнчлэлээ идэвхжүүлнэ үү."}
            </div>
          ) : null}
          {isLoggedIn && !isMembershipActive ? (
            <div className="alert alert-warning mb-3">
              {labels.practicePaymentNotice || "төлбөртэй гишүүнчлэлээ идэвхжүүлнэ үү."}
            </div>
          ) : null}

          {!loading && !error ? (
            filteredTests.length === 0 ? (
              <div className="alert alert-secondary mb-0">
                {labels.listEmpty || "Одоогоор сорил шалгалтын материал алга."}
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table align-middle">
                  <thead>
                    <tr>
                      <th style={{ width: 80 }}>{labels.index || "#"}</th>
                      <th>{labels.name || "Нэр"}</th>
                      <th className="d-none d-md-table-cell">{labels.description || "Тайлбар"}</th>
                      <th style={{ width: 110 }}>{labels.typeColumn || "Төрөл"}</th>
                      <th className="d-none d-lg-table-cell" style={{ minWidth: 140 }}>
                        {labels.examScheduleColumn || "Эхлэх цаг"}
                      </th>
                      <th style={{ width: 180 }}>{labels.updatedAt || "Шинэчлэгдсэн"}</th>
                      <th style={{ width: 140 }} />
                    </tr>
                  </thead>
                  <tbody>
                    {filteredTests.map((test, idx) => {
                      const disabledReason = getOpenDisabledReason(test);
                      const examWindowOk =
                        test.type === "EXAM" && canUseTests && isExamEntryOpen(test.test_dt, test.test_minutes, nowMs);
                      const title =
                        disabledReason ||
                        (test.type === "EXAM" && examWindowOk
                          ? labels.examOpenHint || "Шалгалт нээх (УБ цагаар сул цагийн хугацаанд)"
                          : undefined);

                      return (
                        <tr key={test.id}>
                          <td>{idx + 1}</td>
                          <td className="fw-semibold">{test.name}</td>
                          <td className="text-muted d-none d-md-table-cell">{test.description || "-"}</td>
                          <td>{getTypeDisplayLabel(test.type, labels)}</td>
                          <td className="d-none d-lg-table-cell text-muted small">
                            {test.type === "EXAM" && test.test_dt ? String(test.test_dt) : "—"}
                          </td>
                          <td>{toDateLabel(test.updated_at)}</td>
                          <td className="text-end">
                            <button
                              className="btn btn-primary btn-sm open-test-btn"
                              disabled={!isRowOpenEnabled(test)}
                              onClick={() => navigate(`/tests/${test.id}`)}
                              title={title}
                            >
                              {labels.openTest || "Тест нээх"}
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )
          ) : null}
        </div>
      </div>
    </div>
  );
}
